import { useCallback, useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Markdown } from '../components/Markdown';
import { Orb } from '../components/Orb';
import { ApprovalPreview, compilePins, isHtmlAsset } from '../components/ApprovalPreview';
import type { Pin } from '../components/ApprovalPreview';
import { api } from '../lib/api';
import { useAssetUrl } from '../lib/useAssetUrl';
import { humanizeAction, humanizeResult, quinnStatus } from '../lib/approvalCopy';
import type { Approval, AgentId } from '@shared/types';

const AGENT_NAMES: Record<AgentId, string> = {
  emma: 'Emma',
  milo: 'Milo',
  leo: 'Leo',
  quinn: 'Quinn',
  iris: 'Iris',
  nora: 'Nora',
  grace: 'Grace',
};

/** Quinn's walkthrough video link — needs the tokened asset URL too. */
function WalkthroughLink({ videoKey }: { videoKey: string }) {
  const url = useAssetUrl(videoKey);
  if (!url) return null;
  return (
    <a className="receipt" href={url} target="_blank" rel="noreferrer" style={{ marginTop: 0 }}>
      ▷ walkthrough video
    </a>
  );
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    return String(value);
  }
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function fmt(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** Live T-minus instrument — mono, amber as it tightens, coral once overdue. */
function TMinus({ deadline }: { deadline: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(deadline).getTime();
  if (Number.isNaN(end)) return null;
  const remaining = end - now;
  const overdue = remaining <= 0;
  const tight = !overdue && remaining < 60 * 60 * 1000; // under an hour
  const color = overdue ? 'var(--coral)' : tight ? 'var(--amber)' : 'var(--dim)';
  return (
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      <div className="t-label">Deadline</div>
      <div className="mono" style={{ fontSize: 15, fontWeight: 300, color, marginTop: 4 }}>
        {overdue ? 'OVERDUE' : `T-${fmt(remaining)}`}
      </div>
    </div>
  );
}

/** Verify-after state chip: pending re-walk / verified / failed. */
function VerifyChip({ a }: { a: Approval }) {
  if (!a.executed_at)
    return (
      <span className="chip chip-dim">
        <i />
        Execution queued
      </span>
    );
  const v = a.verify_after;
  if (!v)
    return (
      <span className="chip chip-amber">
        <i />
        Quinn re-walk pending
      </span>
    );
  if (v.passed)
    return (
      <span className="chip chip-mint">
        <i />
        Verified after execution
      </span>
    );
  return (
    <span className="chip chip-coral">
      <i />
      Verify failed
    </span>
  );
}

/** A decided approval — who said yes, what actually ran, and Quinn's re-walk. */
function DecidedCard({ a }: { a: Approval }) {
  const v = a.verify_after;
  const failing = v && !v.passed ? v.checks.filter((c) => !c.ok) : [];
  const decidedLine = a.overridden_by
    ? `overridden by ${a.overridden_by}`
    : `approved by ${a.decided_by ?? 'founder'}`;
  return (
    <div className="card">
      {/* header: identity · title */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
        <Orb agent={a.agent_id} size="md" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="t-label">{AGENT_NAMES[a.agent_id] ?? a.agent_id} · decided</div>
          <h3 style={{ marginTop: 4 }}>{a.title}</h3>
        </div>
      </div>

      {/* decision + verify state */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {a.overridden_by ? (
          <span className="chip chip-violet">
            <i />
            Overridden
          </span>
        ) : (
          <span className="chip chip-mint">
            <i />
            Approved
          </span>
        )}
        <VerifyChip a={a} />
        {v && v.checks.length > 0 && (
          <span className="mono faint" style={{ fontSize: 11 }}>
            {v.checks.filter((c) => c.ok).length}/{v.checks.length} re-walk checks
          </span>
        )}
      </div>

      {a.override_reason && (
        <p className="small" style={{ color: 'var(--amber)', marginBottom: 12 }}>
          Override reason: {a.override_reason}
        </p>
      )}

      {/* decision receipt */}
      <div style={{ marginBottom: 10 }}>
        <div className="t-label">Decision</div>
        <div className="mono" style={{ fontSize: 12, color: 'var(--dim)', marginTop: 6 }}>
          {decidedLine}
          {a.decided_at ? ` · ${new Date(a.decided_at).toLocaleString()}` : ''}
        </div>
      </div>

      {/* execution receipt — plain English first; raw result behind the disclosure */}
      <div style={{ marginBottom: failing.length > 0 ? 10 : 4 }}>
        <div className="t-label">Execution</div>
        {a.executed_at ? (
          (() => {
            const done = humanizeResult(a.action, a.execution_result);
            return (
              <>
                <p style={{ fontSize: 13.5, color: done.failed ? 'var(--coral)' : 'var(--text)', marginTop: 6 }}>
                  {done.text}
                </p>
                <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 4 }}>
                  {new Date(a.executed_at).toLocaleString()}
                </div>
                {a.execution_result != null && (
                  <details style={{ marginTop: 6 }}>
                    <summary className="mono" style={{ fontSize: 11, color: 'var(--faint)', cursor: 'pointer' }}>
                      technical details
                    </summary>
                    <pre
                      className="mono"
                      style={{
                        fontSize: 11,
                        color: 'var(--dim)',
                        marginTop: 6,
                        background: 'var(--panel-2)',
                        border: '1px solid var(--line)',
                        borderRadius: 'var(--r-ctl)',
                        padding: '8px 10px',
                        maxHeight: 200,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {safeJson(a.execution_result)}
                    </pre>
                  </details>
                )}
              </>
            );
          })()
        ) : (
          <div className="mono" style={{ fontSize: 12, color: 'var(--dim)', marginTop: 6 }}>
            not executed yet — the runner picks this up.
          </div>
        )}
      </div>

      {failing.length > 0 && (
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 }}>
          {failing.map((c, i) => (
            <li key={i} className="mono" style={{ fontSize: 11.5, color: 'var(--coral)' }}>
              ✕ {c.label}
              {c.detail ? ` — ${c.detail}` : ''}
            </li>
          ))}
        </ul>
      )}

      <div className="receipt">
        #{a.id} · {AGENT_NAMES[a.agent_id] ?? a.agent_id} · {new Date(a.created_at).toLocaleString()}
      </div>
    </div>
  );
}

interface PendingCardProps {
  a: Approval;
  busy: boolean;
  onApprove: () => void;
  onOverride: () => void;
  /** Fires the changes-requested API call with the final comment. */
  onRequestChanges: (comment: string) => void;
}

/**
 * A pending approval — the founder's design canvas. HTML preview assets render
 * as the actual email/page (sandboxed iframe) with code view + annotation pins;
 * "Request changes" compiles the pins into the comment.
 */
function PendingCard({ a, busy, onApprove, onOverride, onRequestChanges }: PendingCardProps) {
  const name = AGENT_NAMES[a.agent_id] ?? a.agent_id;
  const qa = a.quinn_report;
  const passed = !!qa?.passed;
  const q = quinnStatus(qa); // tells "Quinn flagged N issues" apart from "not reviewed yet"
  const checks = qa?.checks ?? [];
  const failing = checks.filter((c) => !c.ok);

  const previews = a.preview_assets ?? [];
  const htmlKeys = previews.filter(isHtmlAsset);
  const mediaKeys = previews.filter((k) => !isHtmlAsset(k));

  // Annotation pins, keyed per HTML asset — compiled into the changes comment.
  const [pinsByAsset, setPinsByAsset] = useState<Record<string, Pin[]>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [comment, setComment] = useState('');

  function compiledAnnotations(): string {
    const parts: string[] = [];
    for (const key of htmlKeys) {
      const pins = pinsByAsset[key] ?? [];
      if (pins.length === 0) continue;
      const text = compilePins(pins);
      // With several previews, prefix each line with the file so Milo/Leo know which one.
      parts.push(
        htmlKeys.length > 1
          ? text
              .split('\n')
              .map((l) => `[${key.split('/').pop()}] ${l}`)
              .join('\n')
          : text
      );
    }
    return parts.join('\n');
  }

  function handleChangesClick() {
    const compiled = compiledAnnotations();
    if (compiled) {
      // Pre-fill from the pins — the founder edits before sending.
      setComment(compiled);
      setComposerOpen(true);
      return;
    }
    // No pins — the plain path stays exactly as it was.
    const c = window.prompt(`What should ${name} change?`);
    if (c === null) return; // cancelled
    onRequestChanges(c.trim());
  }

  return (
    <div className="card">
      {/* header: identity · title · T-minus */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
        <Orb agent={a.agent_id} size="md" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="t-label">{name} · needs approval</div>
          <h3 style={{ marginTop: 4 }}>{a.title}</h3>
        </div>
        {a.deadline_at && <TMinus deadline={a.deadline_at} />}
      </div>

      {a.summary_md && (
        <Markdown text={a.summary_md} className="dim" style={{ fontSize: 13.5, marginBottom: 12 }} />
      )}

      {/* preview area — HTML assets render full-width as the actual thing */}
      {htmlKeys.map((key) => (
        <ApprovalPreview
          key={key}
          assetKey={key}
          pins={pinsByAsset[key] ?? []}
          onPinsChange={(pins) => setPinsByAsset((prev) => ({ ...prev, [key]: pins }))}
        />
      ))}
      {mediaKeys.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))',
            gap: 10,
            marginBottom: 14,
          }}
        >
          {mediaKeys.map((key) => (
            <ApprovalPreview key={key} assetKey={key} />
          ))}
        </div>
      )}

      {/* Quinn strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <span className={`chip chip-${q.tone}`}>
          <i />
          {q.label}
        </span>
        {checks.length > 0 && (
          <span className="mono faint" style={{ fontSize: 11 }}>
            {checks.filter((c) => c.ok).length}/{checks.length} checks
          </span>
        )}
        {qa?.video_key && <WalkthroughLink videoKey={qa.video_key} />}
      </div>

      {!passed && failing.length > 0 && (
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
          {failing.map((c, i) => (
            <li key={i} className="mono" style={{ fontSize: 11.5, color: 'var(--coral)' }}>
              ✕ {c.label}
              {c.detail ? ` — ${c.detail}` : ''}
            </li>
          ))}
        </ul>
      )}

      {/* action line — plain English, raw action behind the disclosure */}
      <div style={{ marginBottom: 4 }}>
        <div className="t-label">On approve</div>
        <p style={{ fontSize: 13.5, color: 'var(--text)', marginTop: 6 }}>{humanizeAction(a.action)}</p>
        <details style={{ marginTop: 6 }}>
          <summary className="mono" style={{ fontSize: 11, color: 'var(--faint)', cursor: 'pointer' }}>
            technical details
          </summary>
          <pre
            className="mono"
            style={{
              fontSize: 11,
              color: 'var(--dim)',
              marginTop: 6,
              background: 'var(--panel-2)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-ctl)',
              padding: '8px 10px',
              maxHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {safeJson(a.action)}
          </pre>
        </details>
      </div>

      <div className="receipt">
        #{a.id} · {name} · {new Date(a.created_at).toLocaleString()}
      </div>

      {/* actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
        <button className="btn btn-primary" disabled={busy} onClick={onApprove}>
          Approve
        </button>
        <button className="btn btn-line" disabled={busy} onClick={handleChangesClick}>
          Request changes
        </button>
        {!passed && (
          <button className="btn btn-danger" disabled={busy} onClick={onOverride}>
            Override Quinn
          </button>
        )}
      </div>

      {/* annotation composer — pre-filled from the pins, editable before sending */}
      {composerOpen && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
          <div className="t-label">Request changes — from your pins (edit before sending)</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={Math.min(8, Math.max(3, comment.split('\n').length + 1))}
            style={{
              width: '100%',
              marginTop: 8,
              padding: '10px 12px',
              borderRadius: 'var(--r-ctl)',
              border: '1px solid var(--line-2)',
              background: 'var(--panel-2)',
              color: 'var(--text)',
              fontFamily: 'var(--font)',
              fontSize: 13.5,
              lineHeight: 1.5,
              resize: 'vertical',
            }}
            aria-label="Request changes comment"
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
            <button
              className="btn btn-mint sm"
              disabled={busy || !comment.trim()}
              onClick={() => {
                onRequestChanges(comment.trim());
                setComposerOpen(false);
              }}
            >
              Send to {name}
            </button>
            <button className="btn btn-ghost sm" disabled={busy} onClick={() => setComposerOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState<'pending' | 'decided'>('pending');
  const [approvals, setApprovals] = useState<Approval[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decided, setDecided] = useState<Approval[] | null>(null);
  const [decidedError, setDecidedError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await api.get<Approval[]>('/approvals?status=pending');
      setApprovals(rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const loadDecided = useCallback(async () => {
    try {
      const rows = await api.get<Approval[]>('/approvals?status=approved');
      setDecided(rows);
      setDecidedError(null);
    } catch (e) {
      setDecidedError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // Live queue: initial load + gentle poll (T-minus deadlines make it time-sensitive).
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Decided history: polled only while that tab is open (slower — it's a ledger).
  useEffect(() => {
    if (tab !== 'decided') return;
    loadDecided();
    const t = setInterval(loadDecided, 30000);
    return () => clearInterval(t);
  }, [tab, loadDecided]);

  async function act(id: string, run: () => Promise<unknown>) {
    setBusyId(id);
    try {
      await run();
      await load(); // refresh after each action
    } catch (e) {
      window.alert(e instanceof Error ? `Action failed: ${e.message}` : 'Action failed.');
    } finally {
      setBusyId(null);
    }
  }

  function onApprove(a: Approval) {
    act(a.id, () => api.post(`/approvals/${a.id}/approve`));
  }

  function onRequestChanges(a: Approval, comment: string) {
    act(a.id, () => api.post(`/approvals/${a.id}/changes`, { comment }));
  }

  function onOverride(a: Approval) {
    const reason = window.prompt('Reason to override Quinn (required):');
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      window.alert('A reason is required to override Quinn.');
      return;
    }
    act(a.id, () => api.post(`/approvals/${a.id}/override`, { reason: reason.trim() }));
  }

  const pending = approvals?.length ?? 0;
  const actions =
    pending > 0 ? (
      <span className="chip chip-amber">
        <i />
        {pending} pending
      </span>
    ) : undefined;

  return (
    <Layout title="Approvals" actions={actions}>
      {/* pending / decided tabs */}
      <div style={{ marginBottom: 16 }}>
        <div className="toggle" role="tablist" aria-label="Approvals view">
          <button role="tab" aria-selected={tab === 'pending'} className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>
            PENDING{pending > 0 ? ` · ${pending}` : ''}
          </button>
          <button role="tab" aria-selected={tab === 'decided'} className={tab === 'decided' ? 'active' : ''} onClick={() => setTab('decided')}>
            DECIDED
          </button>
        </div>
      </div>

      {tab === 'pending' && (
        <>
          {approvals === null && error && (
            <div className="card">
              <p style={{ color: 'var(--coral)' }}>Couldn't load approvals — {error}</p>
              <button className="btn btn-line" style={{ marginTop: 12 }} onClick={load}>
                Retry
              </button>
            </div>
          )}

          {approvals === null && !error && <p className="small">Loading…</p>}

          {approvals !== null && approvals.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                <Orb agent="quinn" />
              </div>
              <p className="dim">Inbox zero. Quinn's holding the gate — you'll see a card here the moment something needs a human yes.</p>
            </div>
          )}

          {approvals !== null && approvals.length > 0 && (
            <div style={{ display: 'grid', gap: 16 }}>
              {approvals.map((a) => (
                <PendingCard
                  key={a.id}
                  a={a}
                  busy={busyId === a.id}
                  onApprove={() => onApprove(a)}
                  onOverride={() => onOverride(a)}
                  onRequestChanges={(comment) => onRequestChanges(a, comment)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'decided' && (
        <>
          {decided === null && decidedError && (
            <div className="card">
              <p style={{ color: 'var(--coral)' }}>Couldn't load decided approvals — {decidedError}</p>
              <button className="btn btn-line" style={{ marginTop: 12 }} onClick={loadDecided}>
                Retry
              </button>
            </div>
          )}

          {decided === null && !decidedError && <p className="small">Loading…</p>}

          {decided !== null && decided.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                <Orb agent="quinn" size="md" />
              </div>
              <p className="dim">Nothing decided yet — approvals you act on land here with their execution receipt and Quinn's re-walk.</p>
            </div>
          )}

          {decided !== null && decided.length > 0 && (
            <div style={{ display: 'grid', gap: 16 }}>
              {decided.map((a) => (
                <DecidedCard key={a.id} a={a} />
              ))}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
